import React, { useState, useEffect } from "react";
import {
  numberOfMatchesForLane,
  getCellSizeForLane,
} from "../../../publicFunctions/publicHelpers/tournamentViewHelper";

function TournamentMiniMap({
  sizes,
  currentViewport,
  onViewportChange,
  nightMode,
  zoomLevel,
}) {
  // Determine default collapsed state based on screen size
  const getDefaultCollapsedState = () => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768; // Collapsed on tablet and below
    }
    return false; // Default to open on larger devices
  };

  const [isCollapsed, setIsCollapsed] = useState(getDefaultCollapsedState());

  // Recalculate mini map scale when zoom level changes
  useEffect(() => {
    // This will trigger a re-render when zoomLevel changes
  }, [zoomLevel]);

  // Calculate mini map size based on number of lanes (12px per lane)
  const miniMapLaneWidth = 12;
  const miniMapWidth = sizes.numberOfLanes * miniMapLaneWidth;
  const miniMapHeight = (sizes.screenSizeH / sizes.screenSizeW) * miniMapWidth;

  // Calculate scale to fit tournament in the mini map area
  const scaleX = miniMapWidth / sizes.screenSizeW;
  const scaleY = miniMapHeight / sizes.screenSizeH;
  const miniMapScale = Math.min(scaleX, scaleY);

  const scaledWidth = sizes.screenSizeW * miniMapScale;
  const scaledHeight = sizes.screenSizeH * miniMapScale;

  // Calculate viewport rectangle for mini map
  const viewportRect = {
    x: currentViewport.x * miniMapScale,
    y: currentViewport.y * miniMapScale,
    width: currentViewport.width * miniMapScale,
    height: currentViewport.height * miniMapScale,
  };

  const handleMiniMapClick = (event) => {
    if (isCollapsed) return; // Don't handle clicks when collapsed

    const rect = event.currentTarget.getBoundingClientRect();
    const containerX = event.clientX - rect.left;
    const containerY = event.clientY - rect.top;

    // Don't handle clicks in the header area (first 25px)
    if (containerY < 25) return;

    // Adjust Y coordinate to account for header offset
    const adjustedY = containerY - 25;

    // Map the entire mini map container to the full tournament area
    const tournamentX = (containerX / miniMapWidth) * sizes.screenSizeW;
    const tournamentY = (adjustedY / miniMapHeight) * sizes.screenSizeH;

    // Center the viewport on the clicked position
    const newViewportX = Math.max(0, tournamentX - currentViewport.width / 2);
    const newViewportY = Math.max(0, tournamentY - currentViewport.height / 2);

    onViewportChange({
      x: newViewportX,
      y: newViewportY,
      width: currentViewport.width,
      height: currentViewport.height,
    });
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Generate vertical lines for each lane
  const minLineHeight = 4;
  const laneLines = [];
  for (let lane = 1; lane <= sizes.numberOfLanes; lane++) {
    const numMatches = numberOfMatchesForLane(
      lane,
      sizes.totalRounds,
      sizes.numberOfMatchesBottomBracket,
      sizes.numberOfLanes
    );

    const cellSize = getCellSizeForLane(sizes, numMatches);
    const x = (lane - 1) * sizes.laneW * miniMapScale;

    if (numMatches <= 8) {
      // Create dashed line effect - one short line per cell
      for (let cell = 0; cell < numMatches; cell++) {
        const cellY = cell * cellSize.h * miniMapScale;

        const centerLane = Math.ceil(sizes.numberOfLanes / 2);
        if (lane === centerLane && numMatches === 1) {
          // Finalist lane: horizontal line spanning the full lane width
          laneLines.push(
            <div
              key={`minimap-lane-${lane}-cell-${cell}`}
              style={{
                position: "absolute",
                left: x - 2 + "px",
                top: cellY + (cellSize.h * miniMapScale - 2) / 2 + "px",
                width: sizes.laneW * miniMapScale + "px",
                height: "2px",
                backgroundColor: nightMode ? "#c2c4c2" : "#010101",
              }}
            />
          );
        } else {
          // Regular dashed line for other lanes with 8 or fewer cells
          const startY =
            cellY + (cellSize.h * miniMapScale - minLineHeight) / 2;

          laneLines.push(
            <div
              key={`minimap-lane-${lane}-cell-${cell}`}
              style={{
                position: "absolute",
                left: x + miniMapLaneWidth / 2 - 3 + "px",
                top: startY + "px",
                width: "2px",
                height: minLineHeight + "px",
                backgroundColor: nightMode ? "#c2c4c2" : "#010101",
              }}
            />
          );
        }
      }
    } else {
      // Use continuous line for lanes with more than 8 cells
      let startY, lineHeight;

      if (numMatches === 1) {
        // Single match: center the line in the cell
        const cellHeight = cellSize.h * miniMapScale;
        startY = (cellHeight - minLineHeight) / 2;
        lineHeight = minLineHeight;
      } else {
        // Multiple matches: from middle of first to middle of last
        startY = (cellSize.h / 2) * miniMapScale;
        const endY = (numMatches - 0.5) * cellSize.h * miniMapScale;
        lineHeight = Math.max((cellSize.h * miniMapScale) / 4, endY - startY);
      }

      laneLines.push(
        <div
          key={`minimap-lane-${lane}`}
          style={{
            position: "absolute",
            left: x + miniMapLaneWidth / 2 - 3 + "px",
            top: startY + "px",
            width: "2px",
            height: lineHeight + "px",
            backgroundColor: nightMode ? "#c2c4c2" : "#010101",
          }}
        />
      );
    }
  }

  return (
    <div
      style={{
        position: "absolute",
        top: "-23px",
        right: "0px",
        width: isCollapsed ? miniMapWidth + "px" : miniMapWidth + "px",
        height: isCollapsed ? "25px" : miniMapHeight + 25 + "px",
        border: nightMode ? "2px solid #c2c4c2" : "2px solid #010101",
        backgroundColor: nightMode
          ? "rgba(34,34,34,0.95)"
          : "rgba(255,255,255,0.95)",
        cursor: isCollapsed ? "default" : "pointer",
        zIndex: 6,
        transition: "height 0.3s ease",
      }}
      onClick={handleMiniMapClick}
    >
      {/* Header */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "25px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 5px",
          backgroundColor: nightMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)",
          borderBottom: isCollapsed
            ? "none"
            : nightMode
            ? "1px solid #c2c4c2"
            : "1px solid #010101",
          zIndex: 1,
        }}
      >
        <span
          style={{
            fontSize: "10px",
            color: nightMode ? "#c2c4c2" : "#010101",
            fontWeight: "bold",
          }}
        >
          Mini Map
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleCollapse();
          }}
          style={{
            background: "none",
            border: "none",
            color: nightMode ? "#c2c4c2" : "#010101",
            cursor: "pointer",
            fontSize: "12px",
            padding: "2px",
          }}
        >
          {isCollapsed ? "▼" : "▲"}
        </button>
      </div>

      {/* Mini map content (only show when not collapsed) */}
      {!isCollapsed && (
        <div
          style={{
            position: "absolute",
            top: "25px",
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              position: "relative",
              width: scaledWidth + "px",
              height: scaledHeight + "px",
            }}
          >
            {/* Lane lines */}
            {laneLines}

            {/* Viewport rectangle */}
            <div
              style={{
                position: "absolute",
                left: viewportRect.x + "px",
                top: viewportRect.y + "px",
                width: viewportRect.width + "px",
                height: viewportRect.height + "px",
                border: "1px solid #ff0000",
                backgroundColor: "rgba(255,0,0,0.1)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentMiniMap;
